// The three design families every post comes in. A family is a type
// personality (fonts, case, spacing); colour comes separately, from a
// colourway, so any design can be worn in the salon's own colours.
export const FAMILIES = {
  editorial: {
    id: 'editorial',
    name: 'Editorial',
    blurb: 'Serif headlines on calm paper',
    display: 'fraunces', displayWeight: 400, italic: 'fraunces', upper: false, tracking: -0.015, lh: 0.95,
    label: { font: 'figtree', weight: 700, tracking: 0.08 },
    body: { font: 'figtree', weight: 400 },
    strong: { font: 'figtree', weight: 700 },
    radius: 0,
  },
  poster: {
    id: 'poster',
    name: 'Poster',
    blurb: 'Big, bold and loud',
    display: 'archivoBlack', displayWeight: 400, italic: null, upper: true, tracking: -0.01, lh: 0.94,
    label: { font: 'archivo', weight: 700, tracking: 0.06 },
    body: { font: 'archivo', weight: 500 },
    strong: { font: 'archivo', weight: 800 },
    radius: 0,
  },
  quiet: {
    id: 'quiet',
    name: 'Quiet',
    blurb: 'Soft colour and open space',
    display: 'instrument', displayWeight: 400, italic: 'instrument', upper: false, tracking: -0.01, lh: 0.97,
    label: { font: 'jost', weight: 600, tracking: 0.1 },
    body: { font: 'jost', weight: 400 },
    strong: { font: 'jost', weight: 600 },
    radius: 28,
  },
}
export const FAMILY_ORDER = ['editorial', 'poster', 'quiet']

// The layouts: five for each of reviews, new services, the team, and
// questions and tips (from the review and everyday kits), each its own
// composition (post.layout). Their themes set the headline face (a serif, a
// heavy sans or a clean bold sans); the small words share one plain sans.
// Not one of the three styles, so it isn't in FAMILY_ORDER.
export const LAYOUT_TYPES = {
  serif: { display: 'fraunces', displayWeight: 400, italic: 'fraunces', upper: false, tracking: -0.015, lh: 1 },
  heavy: { display: 'archivoBlack', displayWeight: 400, italic: null, upper: false, tracking: -0.01, lh: 0.98 },
  sans: { display: 'archivo', displayWeight: 700, italic: null, upper: false, tracking: -0.015, lh: 1.02 },
}
FAMILIES.layouts = {
  id: 'layouts',
  name: 'Layouts',
  blurb: 'Five layouts for each job, each in four themes',
  ...LAYOUT_TYPES.serif,
  label: { font: 'archivo', weight: 700, tracking: 0.03 },
  body: { font: 'archivo', weight: 400 },
  strong: { font: 'archivo', weight: 700 },
  radius: 0,
}
export const layoutFamily = (type) => ({ ...FAMILIES.layouts, ...(LAYOUT_TYPES[type] || LAYOUT_TYPES.serif) })
// The headline face a theme made from a brand colour wears: the brand's own
// kind of headline font when it has one
const HEAVY = ['archivoBlack', 'anton', 'oswald', 'bricolage']
export const layoutTypeForFont = (font) => {
  if (!font) return 'serif'
  if (HEAVY.includes(font)) return 'heavy'
  return ['fraunces', 'instrument', 'cormorant', 'dmSerif', 'georgia'].includes(font) ? 'serif' : 'sans'
}

// A salon's email look picks its first family, so posts match emails
const LOOK_FAMILY = {
  gloss: 'editorial', atelier: 'editorial', heritage: 'editorial', letter: 'editorial',
  poster: 'poster', fade: 'poster', colourbar: 'poster', zine: 'poster',
  blush: 'quiet', spa: 'quiet', clinic: 'quiet',
}
export const familyForLook = (look) => LOOK_FAMILY[look] || 'editorial'

// Headline fonts a salon can swap in (Text tool). Each keeps the family's
// italic accent when the font has an italic.
export const DISPLAY_FONTS = {
  fraunces: { weight: 400, italic: true, serif: true, lh: 0.95, tracking: -0.015 },
  instrument: { weight: 400, italic: true, serif: true, lh: 0.97, tracking: -0.01 },
  dmSerif: { weight: 400, italic: true, serif: true, lh: 0.98, tracking: -0.01 },
  cormorant: { weight: 600, italic: true, serif: true, lh: 0.95, tracking: -0.005 },
  archivoBlack: { weight: 400, italic: false, serif: false, lh: 0.94, tracking: -0.01 },
  anton: { weight: 400, italic: false, serif: false, lh: 1, tracking: 0.005 },
  oswald: { weight: 600, italic: false, serif: false, lh: 1, tracking: 0 },
  bricolage: { weight: 800, italic: false, serif: false, lh: 0.95, tracking: -0.03 },
  manrope: { weight: 800, italic: false, serif: false, lh: 1, tracking: -0.02 },
}
// Fonts for the small words (labels, details, the booking line)
export const TEXT_FONTS = {
  figtree: { label: 700, body: 400, strong: 700 },
  jost: { label: 600, body: 400, strong: 600 },
  archivo: { label: 700, body: 500, strong: 800 },
  dmSans: { label: 700, body: 400, strong: 700 },
  manrope: { label: 700, body: 500, strong: 800 },
}

// A family with the salon's own fonts swapped in (post.tune.font / textFont)
export const tunedFamily = (family, tune = {}) => {
  const font = DISPLAY_FONTS[tune.font] ? tune.font : null
  const textFont = TEXT_FONTS[tune.textFont] ? tune.textFont : null
  if ((!font || font === family.display) && (!textFont || textFont === family.body.font)) return family
  const f = { ...family, label: { ...family.label }, body: { ...family.body }, strong: { ...family.strong } }
  if (font && font !== family.display) {
    const m = DISPLAY_FONTS[font]
    f.display = font
    f.displayWeight = m.weight
    f.italic = m.italic ? font : null
    f.upper = family.upper && !m.serif
    f.lh = m.lh
    f.tracking = m.tracking
  }
  if (textFont && textFont !== family.body.font) {
    const w = TEXT_FONTS[textFont]
    f.label = { ...f.label, font: textFont, weight: w.label }
    f.body = { font: textFont, weight: w.body }
    f.strong = { font: textFont, weight: w.strong }
  }
  return f
}

// Every font a family draws with, and the weights, for loading before drawing
export const themeFonts = (f) => {
  const list = [
    { key: f.display, weight: f.displayWeight, italic: false },
    { key: f.label.font, weight: f.label.weight, italic: false },
    { key: f.body.font, weight: f.body.weight, italic: false },
    { key: f.strong.font, weight: f.strong.weight, italic: false },
  ]
  if (f.italic) list.push({ key: f.italic, weight: 400, italic: true })
  return list
}
export const familyFonts = (family) => {
  const f = FAMILIES[family] || FAMILIES.editorial
  const list = [
    { key: f.display, weight: f.displayWeight, italic: false },
    { key: f.label.font, weight: f.label.weight, italic: false },
    { key: f.body.font, weight: f.body.weight, italic: false },
    { key: f.strong.font, weight: f.strong.weight, italic: false },
  ]
  if (f.italic) list.push({ key: f.italic, weight: 400, italic: true })
  return list
}
